import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, Check, Pencil, X, Loader2, AlertTriangle } from "lucide-react";
import { getSessionToken } from "@/utils/auth";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });
export const parseUtc = (s) => new Date(/(Z|[+-]\d\d:\d\d)$/.test(s) ? s : `${s}Z`);

function OfficeFlag({ d }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex gap-2" data-testid={`reply-draft-office-${d.draft_id}`}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div><span className="font-semibold">Office handles:</span> {d.reason || "refund, complaint or discount request"}. No AI draft.</div>
    </div>
  );
}

function DraftActions({ d, onDone }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(d.draft);
  const [busy, setBusy] = useState(false);
  const act = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/reply-drafts/${d.draft_id}/${path}`, body, auth());
      toast.success(path === "decline" ? "Draft declined" : "Reply sent");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <>
      {editing ? (
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
          className="w-full rounded-lg border p-2 text-sm" data-testid={`reply-draft-edit-input-${d.draft_id}`} />
      ) : (
        <p className="text-sm text-zinc-900 whitespace-pre-wrap" data-testid={`reply-draft-text-${d.draft_id}`}>{d.draft}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <button disabled={busy || !text.trim()} onClick={() => act("approve", { text: editing ? text : null })}
          className="inline-flex items-center gap-1 rounded-full bg-green-700 text-white px-3 py-1.5 text-xs font-semibold hover:bg-green-800 disabled:opacity-50 transition-colors"
          data-testid={`reply-draft-approve-${d.draft_id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}{editing ? "Send edited" : "Approve & Send"}
        </button>
        {!editing && (
          <button disabled={busy} onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50 transition-colors"
            data-testid={`reply-draft-edit-${d.draft_id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
        )}
        <button disabled={busy} onClick={() => act("decline", {})}
          className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 px-3 py-1.5 text-xs font-semibold hover:bg-red-50 transition-colors"
          data-testid={`reply-draft-decline-${d.draft_id}`}><X className="w-3.5 h-3.5" />Decline</button>
      </div>
    </>
  );
}

export const ReplyDraftCard = ({ customerId, refreshKey }) => {
  const [drafts, setDrafts] = useState([]);
  const load = useCallback(async () => {
    if (!customerId || customerId.startsWith("tech_")) return setDrafts([]);
    try {
      const { data } = await axios.get(`${API}/reply-drafts`, { ...auth(), params: { customer_id: customerId, status: "pending,office_handles", limit: 3 } });
      setDrafts(data);
    } catch { setDrafts([]); }
  }, [customerId]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load, refreshKey]);
  if (!drafts.length) return null;
  const d = drafts[0];
  if (d.status === "office_handles") return <OfficeFlag d={d} />;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="reply-draft-card">
      <div className="flex items-center gap-2 text-xs text-emerald-900">
        <Bot className="w-4 h-4" /><span className="font-semibold">AI draft reply</span>
        <span className="text-emerald-700">#{d.code} · expires {parseUtc(d.expires_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
        {d.flag && <span className="ml-auto rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold" data-testid="reply-draft-flag">{d.flag}</span>}
      </div>
      {d.flag === "needs Brett" && d.reason && <div className="text-xs text-amber-800" data-testid="reply-draft-reason">Why: {d.reason}</div>}
      <DraftActions key={d.draft_id} d={d} onDone={load} />
      {d.sources?.length > 0 && <div className="text-[11px] text-zinc-500">Sources: {d.sources.join(", ")}</div>}
    </div>
  );
};
